import { ColDef } from 'ag-grid-community'
import columnRenderers, { PLACEHOLDER } from 'components/column-renderers'
import { CSVField } from 'hooks/useExportCSV'
import { AccessManagementColumnCode } from '../AccessManagementColumnCode/AccessManagementColumnCode'
import { AccessManagementColumnCodeType } from '../AccessManagementColumnCodeType/AccessManagementColumnCodeType'

export const accessManagementColumns = {
  codeType: {
    headerName: 'Code Type',
    field: 'codeType',
    minWidth: 220,
    cellRenderer: AccessManagementColumnCodeType,
  },
  status: {
    headerName: 'Status',
    field: 'status',
    minWidth: 120,
  },
  codeValue: {
    headerName: 'Code Value',
    cellRenderer: AccessManagementColumnCode,
    colId: 'code',
    minWidth: 140,
  },
  effectiveDate: {
    headerName: 'Effective Date',
    cellRenderer: columnRenderers.date('effectiveDate'),
    minWidth: 270,
    colId: 'effectiveDate',
  },
  expiryDate: {
    headerName: 'Expiry Date',
    cellRenderer: columnRenderers.date('expiryDate'),
    minWidth: 240,
    colId: 'expiryDate',
  },
  numberOfUsers: { headerName: 'Number of Users', field: 'numberOfUsers', minWidth: 200 },
  codeCanBeUsed: {
    headerName: 'Code Not Used',
    valueGetter: (v) => (v.data ? v.data.numberOfUsers - v.data.timesUsed : '-'),
    sortable: false,
  },
  timesUsed: { headerName: 'Code Used', field: 'timesUsed' },
  reason: {
    headerName: 'Reason for Code',
    field: 'reason',
    valueGetter: (value) => {
      if (!value.data) return PLACEHOLDER

      return value.data.reason || PLACEHOLDER
    },
    filter: 'agTextColumnFilter',
  },
  organizationName: {
    headerName: 'Organization Name',
    field: 'organizationName',
    minWidth: 220,
    filter: 'agTextColumnFilter',
    valueGetter: (value) => {
      if (!value.data) return PLACEHOLDER
      if (value.data.organizationName) {
        return value.data.organizationName
      }

      return value.data.organization?.name || PLACEHOLDER
    },
  },
  twoDigitsOrg: {
    headerName: 'Two-Digit Organization Number',
    field: 'twoDigitsOrg',
    minWidth: 300,
    valueGetter: (value) => {
      if (!value.data) return PLACEHOLDER
      if (value.data.twoDigitsOrg) {
        return value.data.twoDigitsOrg
      }

      return value.data.organization?.twoDigits || PLACEHOLDER
    },
  },
  organizationType: {
    headerName: 'Organization Type',
    field: 'organizationType',
    minWidth: 220,
    filter: 'agTextColumnFilter',
    valueGetter: (value) => {
      if (!value.data) return PLACEHOLDER
      if (value.data.organizationType) {
        return value.data.organizationType
      }

      return value.data.organization?.type || PLACEHOLDER
    },
  },
  state: {
    headerName: 'Two-Letter State Location of Trainees',
    field: 'state',
    minWidth: 360,
    valueGetter: (value) => {
      if (!value.data) return PLACEHOLDER
      return value.data.state || value.data.specificState || PLACEHOLDER
    },
  },
  specificUnitName: {
    headerName: 'Specific Unit Name',
    field: 'specificUnitName',
    valueGetter: (value) => {
      if (!value.data) return PLACEHOLDER

      return value.data.specificUnitName || PLACEHOLDER
    },
    minWidth: 260,
    filter: 'agTextColumnFilter',
  },
  twoDigitsSpecific: {
    headerName: 'Two-Digit Specific Unit Letter / Number',
    field: 'specificTwoDigits',
    minWidth: 360,
    valueGetter: (value) => {
      if (!value.data) return PLACEHOLDER

      return value.data.specificTwoDigits || PLACEHOLDER
    },
  },
  duration: {
    headerName: 'Access Code Duration',
    field: 'duration',
    valueGetter: (value) => {
      if (!value.data) return PLACEHOLDER

      return value.data.duration || PLACEHOLDER
    },
    minWidth: 240,
  },
} satisfies Record<string, ColDef>

export const institutionalCodeColumns: ColDef[] = [
  accessManagementColumns.codeType,
  accessManagementColumns.status,
  accessManagementColumns.codeValue,
  accessManagementColumns.effectiveDate,
  accessManagementColumns.expiryDate,
  accessManagementColumns.numberOfUsers,
  accessManagementColumns.codeCanBeUsed,
  accessManagementColumns.timesUsed,
  accessManagementColumns.reason,
  accessManagementColumns.organizationName,
  accessManagementColumns.twoDigitsOrg,
  accessManagementColumns.organizationType,
  accessManagementColumns.state,
  accessManagementColumns.specificUnitName,
  accessManagementColumns.twoDigitsSpecific,
  accessManagementColumns.duration,
]

export const promoCodeColumns: ColDef[] = [
  accessManagementColumns.codeType,
  accessManagementColumns.codeValue,
  accessManagementColumns.timesUsed,
  accessManagementColumns.codeCanBeUsed,
  accessManagementColumns.reason,
  accessManagementColumns.effectiveDate,
  accessManagementColumns.expiryDate,
  accessManagementColumns.numberOfUsers,
]

export const csvInstitutionalCodeColumns: CSVField[] = [
  { label: 'Code Type', value: 'codeType' },
  { label: 'Status', value: 'status' },
  { label: 'Code Value', value: 'codeValue' },
  { label: 'Effective Date', value: 'effectiveDate' },
  { label: 'Expiry Date', value: 'expiryDate' },
  { label: 'Number of Users', value: 'numberOfUsers' },
  { label: 'Code Not Used', value: 'codeCanBeUsed' },
  { label: 'Code Used', value: 'timesUsed' },
  { label: 'Reason for Code', value: 'reason' },
  { label: 'Organization Name', value: 'organizationName' },
  { label: 'Two-Digit Organization Number', value: 'twoDigitsOrg' },
  { label: 'Organization Type', value: 'organizationType' },
  { label: 'Two-Letter State Location of Trainees', value: 'state' },
  { label: 'Specific Unit Name', value: 'specificUnitName' },
  { label: 'Two-Digit Specific Unit Letter / Number', value: 'twoDigitsSpecific' },
  { label: 'Access Code Duration', value: 'duration' },
]

export const csvPromoCodeColumns: CSVField[] = [
  { label: 'Code Type', value: 'codeType' },
  { label: 'Code Value', value: 'codeValue' },
  { label: 'Times Used', value: 'timesUsed' },
  { label: 'Code Can Be Used', value: 'codeCanBeUsed' },
  { label: 'Reason for Code', value: 'reason' },
  { label: 'Effective Date', value: 'effectiveDate' },
  { label: 'Expiry Date', value: 'expiryDate' },
  { label: 'Number of Users', value: 'numberOfUsers' },
]
